import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { CheckCircle } from 'lucide-react';
import styles from './HubSpotConnection.module.css';

// Inline HubSpot logo SVG (no Lucide equivalent)
function HubSpotIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.5 2C11.29 2 9.5 3.79 9.5 6s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm-6 7c-1.65 0-3 1.35-3 3s1.35 3 3 3 3-1.35 3-3-1.35-3-3-3zm0 7c-2.33 0-7 1.17-7 3.5V21h14v-1.5c0-2.33-4.67-3.5-7-3.5zm6 0c-.29 0-.62.02-.97.05.02.01.03.03.04.04 1.14.83 1.93 1.94 1.93 3.41V21h6v-1.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  );
}

export default function HubSpotConnection({ userId }) {
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { fetchConnection(); }, [userId]);

  const fetchConnection = async () => {
    try {
      setLoading(true);
      const session = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/hubspot/connection`, {
        headers: { 'Authorization': `Bearer ${session.data.session?.access_token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConnection(data.connection);
      } else if (response.status === 404) {
        setConnection(null);
      } else {
        throw new Error('Failed to fetch HubSpot connection');
      }
    } catch (err) {
      console.error('Error fetching HubSpot connection:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true); setError(null);
      const session = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/hubspot/auth-url`, {
        headers: { 'Authorization': `Bearer ${session.data.session?.access_token}` }
      });
      if (!response.ok) throw new Error('Failed to get HubSpot authorization URL');
      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      console.error('Error connecting to HubSpot:', err);
      setError(err.message);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect HubSpot? Meetings will no longer sync to your CRM.')) return;
    try {
      setConnecting(true); setError(null);
      const session = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/hubspot/connection`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.data.session?.access_token}` }
      });
      if (!response.ok) throw new Error('Failed to disconnect HubSpot');
      setConnection(null);
    } catch (err) {
      console.error('Error disconnecting HubSpot:', err);
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.card}>
        <div className={styles.skeleton}>
          <div className={styles.skeletonLine} />
          <div className={styles.skeletonBlock} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <div className={styles.titleRow}>
            <HubSpotIcon className={styles.hubspotIcon} />
            <h3 className={styles.title}>HubSpot CRM</h3>
          </div>
          <p className={styles.subtitle}>Sync meetings to your CRM automatically</p>
        </div>
        {connection && <span className={styles.connectedBadge}>Connected</span>}
      </div>

      {error && (
        <div className={styles.errorBox}>
          <p className={styles.errorText}>{error}</p>
        </div>
      )}

      {connection ? (
        <div className={styles.body}>
          <div className={styles.detailsBox}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Portal:</span>
              <span className={styles.detailValue}>{connection.hub_domain || 'Connected'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Connected:</span>
              <span className={styles.detailValue}>{new Date(connection.connected_at).toLocaleDateString()}</span>
            </div>
            {connection.last_used_at && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Last Used:</span>
                <span className={styles.detailValue}>{new Date(connection.last_used_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          <div className={styles.syncNote}>
            <CheckCircle size={20} className={styles.syncIcon} aria-hidden="true" />
            <span>Meetings will automatically sync to HubSpot when booked</span>
          </div>

          <button onClick={handleDisconnect} disabled={connecting} className={styles.disconnectBtn}>
            {connecting ? 'Disconnecting...' : 'Disconnect HubSpot'}
          </button>
        </div>
      ) : (
        <div className={styles.body}>
          <ul className={styles.featureList}>
            <li className={styles.featureItem}>
              <CheckCircle size={20} className={styles.featureIcon} aria-hidden="true" />
              <span>Auto-create meeting engagements in HubSpot</span>
            </li>
            <li className={styles.featureItem}>
              <CheckCircle size={20} className={styles.featureIcon} aria-hidden="true" />
              <span>Link meetings to existing contacts</span>
            </li>
            <li className={styles.featureItem}>
              <CheckCircle size={20} className={styles.featureIcon} aria-hidden="true" />
              <span>Track booking activity in your CRM</span>
            </li>
          </ul>

          <button onClick={handleConnect} disabled={connecting} className={styles.connectBtn}>
            {connecting ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                Connecting...
              </>
            ) : (
              <>
                <HubSpotIcon className={styles.connectIcon} />
                Connect HubSpot
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
